import type { Transfer } from './types';

export interface ActivityBucket {
  index: number;
  startMs: number;
  endMs: number;
  count: number;
}

const DEFAULT_BUCKET_COUNT = 12;

/**
 * Buckets *only the transfers already loaded on the client* into equal time
 * slices — this is a scan aid over what's on screen, never a claim about a
 * complete period total. Transfers with no timestamp (pending) can't be
 * placed on a timeline and are left out of the buckets entirely; callers
 * must keep showing them regardless of any bucket selection.
 */
export function bucketTransfersByTime(
  transfers: Transfer[],
  bucketCount: number = DEFAULT_BUCKET_COUNT,
): ActivityBucket[] {
  const timedMs = transfers
    .map((t) => (t.timestamp ? new Date(t.timestamp).getTime() : null))
    .filter((ms): ms is number => ms !== null && !Number.isNaN(ms));

  if (timedMs.length === 0) return [];

  const minMs = Math.min(...timedMs);
  const maxMs = Math.max(...timedMs);
  const span = Math.max(maxMs - minMs, 1);
  const bucketSize = span / bucketCount;

  const buckets: ActivityBucket[] = Array.from({ length: bucketCount }, (_, index) => ({
    index,
    startMs: minMs + index * bucketSize,
    endMs: minMs + (index + 1) * bucketSize,
    count: 0,
  }));

  for (const ms of timedMs) {
    const index = Math.min(bucketCount - 1, Math.floor((ms - minMs) / bucketSize));
    buckets[index].count += 1;
  }

  return buckets;
}

/** True if a transfer's timestamp falls inside the given bucket's range. */
export function isTransferInBucket(transfer: Transfer, bucket: ActivityBucket): boolean {
  if (!transfer.timestamp) return false;
  const ms = new Date(transfer.timestamp).getTime();
  if (Number.isNaN(ms)) return false;
  return ms >= bucket.startMs && ms <= bucket.endMs;
}
